import React, { useEffect, useMemo, useState } from 'react'
import DropdownInput, { DropdownOption } from './DropdownInput'
import datasetService, { DatasetProjectNode as DsNode } from '../services/datasetService'

export interface DatasetProjectFormData {
  projectName: string
  projectDescription?: string
  // 2단계는 "작업(모델)"이다. 데이터셋 버전은 모델 화면에서 만든다 —
  // 프로젝트 생성 시점에 데이터셋명을 받으면 모델 단계를 건너뛰어
  // 미분류 데이터셋이 되고, 트리(프로젝트 > 모델)에도 나타나지 않는다.
  taskName?: string
  taskDescription?: string
  existingProjectId?: number
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: DatasetProjectFormData) => Promise<void> | void
  checkDuplicate?: (data: { projectName: string; taskName?: string }) => Promise<'ok' | 'duplicateProject' | 'duplicatePair'> | 'ok' | 'duplicateProject' | 'duplicatePair'
  canCreateNewProject: boolean
}

export default function CreateDatasetProjectModal({ isOpen, onClose, onSubmit, checkDuplicate, canCreateNewProject }: Props) {
  const MAX_DESC = 1000
  const [projectName, setProjectName] = useState('')
  const [projectDescription, setProjectDescription] = useState('')
  const [taskName, setTaskName] = useState('')
  const [taskDescription, setTaskDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nodes, setNodes] = useState<DsNode[]>([])
  const [selProject, setSelProject] = useState<DsNode | null>(null)
  const [selTask, setSelTask] = useState<DsNode | null>(null)

  const isValid = useMemo(() => projectName.trim().length > 0, [projectName])

  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      try { const list = await datasetService.listProjects(); setNodes(list) } catch { setNodes([]) }
      setSelProject(null); setSelTask(null)
      setProjectName(''); setProjectDescription(''); setTaskName(''); setTaskDescription('')
      setError(null)
    })()
  }, [isOpen])

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (!isValid) return
    setError(null)
    try {
      setSubmitting(true)
      if (checkDuplicate) {
        const verdict = await checkDuplicate({ projectName: projectName.trim(), taskName: taskName.trim() || undefined })
        if (verdict === 'duplicateProject') {
          setError('동일한 프로젝트명이 이미 존재합니다.')
          setSubmitting(false)
          return
        }
        if (verdict === 'duplicatePair') {
          setError('동일한 프로젝트/작업 조합이 이미 존재합니다.')
          setSubmitting(false)
          return
        }
      }
      await onSubmit({
        projectName: projectName.trim(),
        projectDescription: projectDescription.trim() || undefined,
        taskName: taskName.trim() || undefined,
        taskDescription: taskDescription.trim() || undefined,
        existingProjectId: selProject ? selProject.id : undefined,
      })
      alert('✅ 학습데이터셋 프로젝트가 생성되었습니다!')
      setSubmitting(false)
      onClose()
      setProjectName('')
      setProjectDescription('')
      setTaskName('')
      setTaskDescription('')
    } catch (e: any) {
      setSubmitting(false)
      setError(e?.message || '생성 중 오류가 발생했습니다.')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
        <div className="text-lg font-semibold mb-2">프로젝트 생성</div>
        <div className="text-xs text-gray-500 mb-4">프로젝트명(1단계)만 입력해도 생성할 수 있으며, 작업명(2단계)을 함께 입력하면 동시에 생성됩니다. 학습데이터셋(3단계)은 작업 화면에서 만듭니다.</div>

        <div className="space-y-3">
          {/* 프로젝트명 */}
          <div>
            <DropdownInput
              label={<>프로젝트명 <span className="text-red-500 ml-0.5">*</span></>}
              value={projectName}
              onChange={(v) => setProjectName(v)}
              options={nodes.filter(n => n.kind === 'project').map(n => ({ id: n.id, name: n.name, description: n.description || '' })) as DropdownOption[]}
              placeholder={"예: 자율주행"}
              inputDisabled={!!selProject || submitting || !canCreateNewProject}
              dropdownDisabled={submitting}
              onPickExisting={(opt) => {
                const node = nodes.find(n => n.id === Number(opt.id) && n.kind === 'project') || null
                setSelProject(node)
                setProjectName(opt.name)
                setProjectDescription((node?.description as any) || '')
                // 프로젝트 변경 시 작업명 초기화 및 후보 목록 갱신
                setSelTask(null); setTaskName(''); setTaskDescription('')
              }}
              onDirectInput={canCreateNewProject ? () => {
                setSelProject(null)
                setProjectName('')
                setProjectDescription('')
                setSelTask(null); setTaskName(''); setTaskDescription('')
              } : undefined}
            />
            <label className="block text-sm text-gray-800 mb-1 mt-2">프로젝트 설명 (선택)</label>
            <textarea
              value={selProject ? ((selProject?.description as any) || '') : (projectDescription || '')}
              onChange={(e) => { if (!selProject) setProjectDescription(e.target.value) }}
              className={`w-full border rounded px-3 py-2 text-sm ${selProject ? 'border-gray-200 bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed' : 'border-gray-300'}`}
              rows={2}
              placeholder="이 프로젝트에 대한 설명"
              readOnly={!!selProject}
              aria-disabled={!!selProject}
              maxLength={MAX_DESC}
            />
          </div>

          {/* 작업(모델)명 */}
          <div>
            <DropdownInput
              label={"작업명 (선택)"}
              value={taskName}
              onChange={(v) => setTaskName(v)}
              options={(() => {
                if (!selProject) return []
                // 후보는 그 프로젝트의 기존 작업(모델) 목록
                return nodes
                  .filter(n => n.kind === 'task' && n.parent_id === selProject.id)
                  .map(n => ({ id: n.id, name: n.name, description: n.description || '' })) as DropdownOption[]
              })()}
              placeholder={"예: adcu_seg"}
              inputDisabled={!!selTask || submitting}
              dropdownDisabled={submitting || !selProject}
              onPickExisting={(opt) => {
                const node = nodes.find(n => n.id === Number(opt.id) && n.kind === 'task') || null
                setSelTask(node)
                setTaskName(opt.name)
                setTaskDescription((node?.description as any) || '')
              }}
              onDirectInput={() => {
                setSelTask(null)
                setTaskName('')
                setTaskDescription('')
              }}
            />
            <label className="block text-sm text-gray-800 mb-1 mt-2">작업 설명 (선택)</label>
            <textarea
              value={selTask ? ((selTask?.description as any) || '') : (taskDescription || '')}
              onChange={(e) => { if (!selTask) setTaskDescription(e.target.value) }}
              className={`w-full border rounded px-3 py-2 text-sm ${selTask ? 'border-gray-200 bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed' : 'border-gray-300'}`}
              rows={2}
              placeholder="이 작업에 대한 설명"
              readOnly={!!selTask}
              aria-disabled={!!selTask}
              maxLength={MAX_DESC}
            />
          </div>

          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50" onClick={onClose} disabled={submitting}>취소</button>
          <button
            className={`px-3 py-1 text-sm rounded text-white ${isValid ? 'bg-gray-800 hover:bg-gray-900' : 'bg-gray-400 cursor-not-allowed'}`}
            disabled={!isValid || submitting}
            onClick={handleSubmit}
          >
            {submitting ? '생성 중...' : '생성'}
          </button>
        </div>
      </div>
    </div>
  )
}


